import { trackEvent } from "@/services/posthog";
import { DEFAULT_PRO_SECTIONS, DEFAULT_PRO_TEMPLATES, templateById } from "./constants";

/**
 * Rastreio do perfil publico no PostHog de talentos.
 *
 * Todo evento sai com o prefixo "public_profile_" e leva o retrato do perfil na hora
 * (propriedades "profile_*"): plano, modelo no ar e no rascunho, secoes, estilo,
 * modo demonstracao, aparelho. Assim qualquer evento se fatia por plano ou modelo
 * sem precisar cruzar com outro.
 *
 * O que muda no rascunho (modelo, cor, secao, item, texto...) e rastreado num lugar
 * so, na action `edit` do store, comparando antes e depois: ver trackDraftChange.
 */

const TEXT_DEBOUNCE_MS = 1500;
const FLAG_OPTIONS = [
  "search_indexing",
  "hide_branding",
  "hide_resume_download",
  "hide_share",
  "show_experience_details",
  "game_only",
];
const PRO_FLAGS = ["hide_branding", "hide_resume_download", "hide_share"];
const TEMPLATE_OPTIONS = ["character", "platformer_scene", "presentation_backdrop", "presentation_pace"];
const TEXT_FIELDS = ["headline", "tagline"];
const THEME_KEYS = ["accent", "font", "photoShape", "buttonShape"];

// Primeiro segmento da URL do app do talento e o company_label (/{label}/perfil-publico).
function companyLabelFromUrl() {
  try {
    return window.location.pathname.split("/").filter(Boolean)[0] || null;
  } catch (e) {
    return null;
  }
}

function screenFromUrl() {
  try {
    const parts = window.location.pathname.split("/").filter(Boolean);
    return parts.slice(1).join("/") || null;
  } catch (e) {
    return null;
  }
}

function viewport() {
  const width = typeof window !== "undefined" ? window.innerWidth : null;
  return { profile_viewport: width !== null && width < 768 ? "mobile" : "desktop", profile_viewport_width: width };
}

function proTemplatesOf(editor) {
  return (editor && editor.pro && editor.pro.templates) || DEFAULT_PRO_TEMPLATES;
}

function proSectionsOf(editor) {
  return (editor && editor.pro && editor.pro.sections) || DEFAULT_PRO_SECTIONS;
}

function proFeaturesOf(draft, editor) {
  if (!draft) return [];
  const used = [];
  if (proTemplatesOf(editor).includes(draft.template)) used.push(draft.template);
  (draft.sections || []).forEach((section) => {
    if (section.visible && proSectionsOf(editor).includes(section.id)) used.push(section.id);
  });
  PRO_FLAGS.forEach((flag) => {
    if (draft[flag]) used.push(flag);
  });
  return used;
}

/** O perfil agora, a partir do state do modulo publicProfile. */
export function profileContext(state) {
  const s = state || {};
  const editor = s.editor || null;
  const page = (editor && editor.page) || null;
  const plan = (editor && editor.plan) || {};
  const prices = (editor && editor.prices) || {};
  const counts = (editor && editor.counts) || {};
  const draft = s.draft || null;
  const source = draft || page || {};
  const sections = Array.isArray(source.sections) ? source.sections : [];
  const theme = source.theme || {};
  const hidden = source.hidden_items || {};
  const proTemplates = proTemplatesOf(editor);
  const proFeatures = proFeaturesOf(draft, editor);

  return {
    company_label: companyLabelFromUrl(),
    profile_screen: screenFromUrl(),
    ...viewport(),
    profile_loaded: !!page,
    profile_public_id: page ? page.public_id : null,
    profile_path: page ? page.path : null,
    profile_url: page ? page.url : null,
    profile_has_name_address: !!(page && page.slug),
    profile_is_published: !!(page && page.is_published),
    profile_ever_published: !!(page && (page.is_published || page.published_at)),
    profile_published_at: (page && page.published_at) || null,
    profile_plan: plan.is_pro ? "pro" : "free",
    profile_plan_type: plan.type || null,
    profile_plan_status: plan.status || null,
    profile_plan_cancel_at_period_end: !!plan.cancel_at_period_end,
    profile_plan_period_end: plan.current_period_end || null,
    profile_price_monthly_cents: prices.monthly_cents || null,
    profile_price_lifetime_cents: prices.lifetime_cents || null,
    profile_live_template: page ? page.template : null,
    profile_live_template_is_pro: !!(page && proTemplates.includes(page.template)),
    profile_draft_template: draft ? draft.template : null,
    profile_draft_template_is_pro: !!(draft && proTemplates.includes(draft.template)),
    profile_template_pending: !!s.templatePending,
    profile_demo_mode: !plan.is_pro && proFeatures.length > 0,
    profile_pro_features_in_use: proFeatures,
    profile_section_order: sections.map((section) => section.id),
    profile_visible_sections: sections.filter((section) => section.visible).map((section) => section.id),
    profile_hidden_sections: sections.filter((section) => !section.visible).map((section) => section.id),
    profile_accent: theme.accent || null,
    profile_font: theme.font || null,
    profile_photo_shape: theme.photoShape || null,
    profile_button_shape: theme.buttonShape || null,
    profile_search_indexing: !!source.search_indexing,
    profile_hide_branding: !!source.hide_branding,
    profile_hide_resume_download: !!source.hide_resume_download,
    profile_hide_share: !!source.hide_share,
    profile_show_experience_details: !!source.show_experience_details,
    profile_has_headline: !!source.headline,
    profile_has_tagline: !!source.tagline,
    profile_hidden_experiences: Array.isArray(hidden.experiences) ? hidden.experiences.length : 0,
    profile_hidden_education: Array.isArray(hidden.education) ? hidden.education.length : 0,
    profile_character: source.character || null,
    profile_platformer_scene: source.platformer_scene || null,
    profile_presentation_backdrop: source.presentation_backdrop || null,
    profile_presentation_pace: source.presentation_pace || null,
    profile_game_only: !!source.game_only,
    profile_count_experiences: counts.experiences !== undefined ? counts.experiences : null,
    profile_count_education: counts.education !== undefined ? counts.education : null,
    profile_count_languages: counts.languages !== undefined ? counts.languages : null,
    profile_count_skills: counts.skills !== undefined ? counts.skills : null,
    profile_count_links: counts.links !== undefined ? counts.links : null,
    profile_has_video: counts.has_video === undefined ? null : !!counts.has_video,
    profile_has_audio: counts.has_audio === undefined ? null : !!counts.has_audio,
    profile_has_behavioral: counts.has_behavioral === undefined || counts.has_behavioral === null ? null : !!counts.has_behavioral,
    profile_save_status: s.saveStatus || null,
  };
}

/**
 * Propriedades da pessoa no PostHog ($set): servem para montar coorte
 * ("quem e PRO", "quem publicou") sem depender de um evento especifico.
 */
export function personProperties(state) {
  const ctx = profileContext(state);
  if (!ctx.profile_loaded) return {};
  return {
    public_profile_plan: ctx.profile_plan,
    public_profile_plan_type: ctx.profile_plan_type,
    public_profile_is_published: ctx.profile_is_published,
    public_profile_template: ctx.profile_live_template,
    public_profile_url: ctx.profile_url,
  };
}

/** Manda um evento do perfil publico com o contexto completo. */
export function trackPublicProfile(state, event, props = {}, { setPerson = false } = {}) {
  const name = event.indexOf("public_profile_") === 0 ? event : `public_profile_${event}`;
  const payload = { ...profileContext(state), ...props };
  if (setPerson) payload.$set = personProperties(state);
  trackEvent(name, payload);
}

// ---------------------------------------------------------------------------
// Mudancas no rascunho
// ---------------------------------------------------------------------------

// Texto (titulo e frase): um evento quando a pessoa para de digitar, nao a cada tecla.
const textTimers = {};
const textStart = {};

function trackText(getState, field, before, after, meta) {
  if (!(field in textStart)) textStart[field] = before || "";
  clearTimeout(textTimers[field]);
  textTimers[field] = setTimeout(() => {
    const previous = textStart[field];
    delete textStart[field];
    const value = after || "";
    if (previous === value) return;
    trackPublicProfile(getState(), "public_profile_text_edited", {
      field,
      value,
      previous_value: previous,
      length: value.length,
      previous_length: previous.length,
      cleared: !value,
      first_time: !previous,
      ...meta,
    });
  }, TEXT_DEBOUNCE_MS);
}

function sameList(a, b) {
  return a.length === b.length && a.every((item, index) => item === b[index]);
}

/**
 * Compara o rascunho antes e depois de um `edit` e manda um evento por mudanca.
 * `getState` devolve o state ja atualizado (o contexto sai com o valor novo);
 * `meta` vem do componente (ex.: { origin: "template_picker" }).
 */
export function trackDraftChange(getState, before, patch, meta = {}) {
  if (!before || !patch) return;
  const state = getState();
  const editor = state.editor;
  const proTemplates = proTemplatesOf(editor);
  const proSections = proSectionsOf(editor);

  if ("template" in patch && patch.template !== before.template) {
    const page = editor && editor.page;
    trackPublicProfile(state, "public_profile_template_changed", {
      from: before.template,
      to: patch.template,
      from_name: templateById(before.template).name,
      to_name: templateById(patch.template).name,
      from_is_pro: proTemplates.includes(before.template),
      to_is_pro: proTemplates.includes(patch.template),
      // Perfil no ar: o modelo novo so vai para o link depois de "Publicar com este modelo".
      goes_live_now: !(page && page.is_published),
      ...meta,
    });
  }

  if (patch.theme) {
    const from = before.theme || {};
    THEME_KEYS.forEach((key) => {
      if (key in patch.theme && patch.theme[key] !== from[key]) {
        trackPublicProfile(state, "public_profile_style_changed", {
          setting: key,
          from: from[key] || null,
          to: patch.theme[key],
          template: patch.template || before.template,
          ...meta,
        });
      }
    });
  }

  if (Array.isArray(patch.sections)) {
    const prev = before.sections || [];
    const next = patch.sections;
    next.forEach((section) => {
      const old = prev.find((item) => item.id === section.id);
      if (old && !!old.visible !== !!section.visible) {
        trackPublicProfile(state, "public_profile_section_toggled", {
          section: section.id,
          visible: !!section.visible,
          section_is_pro: proSections.includes(section.id),
          visible_sections_count: next.filter((item) => item.visible).length,
          template: before.template,
          ...meta,
        });
      }
    });
    const prevOrder = prev.map((section) => section.id);
    const nextOrder = next.map((section) => section.id);
    if (!sameList(prevOrder, nextOrder)) {
      // A secao arrastada e a que mais mudou de lugar.
      let moved = null;
      let distance = 0;
      nextOrder.forEach((id, index) => {
        const d = Math.abs(prevOrder.indexOf(id) - index);
        if (d > distance) {
          distance = d;
          moved = id;
        }
      });
      trackPublicProfile(state, "public_profile_sections_reordered", {
        section: moved,
        from_position: moved ? prevOrder.indexOf(moved) + 1 : null,
        to_position: moved ? nextOrder.indexOf(moved) + 1 : null,
        from_order: prevOrder,
        to_order: nextOrder,
        template: before.template,
        ...meta,
      });
    }
  }

  if (patch.hidden_items) {
    const items = (editor && editor.items) || {};
    ["experiences", "education"].forEach((key) => {
      const prev = (before.hidden_items && before.hidden_items[key]) || [];
      const next = patch.hidden_items[key] || [];
      const list = items[key] || [];
      const changed = [
        ...next.filter((id) => !prev.includes(id)).map((id) => ({ id, visible: false })),
        ...prev.filter((id) => !next.includes(id)).map((id) => ({ id, visible: true })),
      ];
      changed.forEach(({ id, visible }) => {
        const item = list.find((entry) => entry.id === id) || {};
        trackPublicProfile(state, "public_profile_item_toggled", {
          section: key === "experiences" ? "experience" : "education",
          item_id: id,
          item_title: item.title || null,
          item_period: item.period || null,
          visible,
          hidden_count: next.length,
          total_count: list.length,
          ...meta,
        });
      });
    });
  }

  FLAG_OPTIONS.forEach((flag) => {
    if (flag in patch && !!patch[flag] !== !!before[flag]) {
      trackPublicProfile(state, "public_profile_option_toggled", {
        option: flag,
        enabled: !!patch[flag],
        option_is_pro: PRO_FLAGS.includes(flag),
        template: before.template,
        ...meta,
      });
    }
  });

  TEMPLATE_OPTIONS.forEach((option) => {
    if (option in patch && patch[option] !== before[option]) {
      trackPublicProfile(state, "public_profile_template_option_changed", {
        option,
        from: before[option] || null,
        to: patch[option] || null,
        template: before.template,
        ...meta,
      });
    }
  });

  TEXT_FIELDS.forEach((field) => {
    if (field in patch && patch[field] !== before[field]) {
      trackText(getState, field, before[field], patch[field], meta);
    }
  });

  // Entrou ou saiu do modo demonstracao (gratis usando recurso PRO na previa).
  const plan = (editor && editor.plan) || {};
  if (!plan.is_pro) {
    const wasDemo = proFeaturesOf(before, editor).length > 0;
    const features = proFeaturesOf(state.draft, editor);
    const isDemo = features.length > 0;
    if (wasDemo !== isDemo) {
      trackPublicProfile(state, isDemo ? "public_profile_demo_mode_started" : "public_profile_demo_mode_ended", {
        pro_features: isDemo ? features : proFeaturesOf(before, editor),
        ...meta,
      });
    }
  }
}
